import { __awaiter, __generator } from "tslib";
import * as AugLoopUtils from "./utils/AugLoopUtils";
import { SpeechToTextPartialResult, SpeechToTextFinalResult, SpeechSessionEvent, SpeechSessionEventIds, SpeechQualityEvent, DictationSettingsManager, IEventName, VoiceResponseVersion, SpeechErrorEvent, SpeechErrorEventState, SpeechErrorEventImpact, } from "@augloop-types/voice";
import { ULS, TraceLevel, LogCategory } from "@1js/office-online-telemetry";
import { sendTelemetryEvent } from "@1js/office-online-otel";
import { getBooleanFeatureGate } from "@1js/office-online-app-platform";
var dictationUserPerceivedLatencies = function () {
    return getBooleanFeatureGate("Microsoft.Office.WordOnline.DictationUserPerceivedLatencies", 
    /*defaultValue*/ false);
};
export var SpeechToTextResultType;
(function (SpeechToTextResultType) {
    SpeechToTextResultType[SpeechToTextResultType["FinalResult"] = 0] = "FinalResult";
    SpeechToTextResultType[SpeechToTextResultType["PartialResult"] = 1] = "PartialResult";
})(SpeechToTextResultType || (SpeechToTextResultType = {}));
// Represents a client that will receive speech-generated text from AL.
var SpeechToTextClient = /** @class */ (function () {
    function SpeechToTextClient(runtimeManager, fdSpeechClientObserver, useFrontdoorWorkflow, startRecognitionInSpeechToTextClient, enableSetAnnotationsFromALVoiceClient) {
        if (useFrontdoorWorkflow === void 0) { useFrontdoorWorkflow = true; }
        if (startRecognitionInSpeechToTextClient === void 0) { startRecognitionInSpeechToTextClient = true; }
        var _this = this;
        var _a, _b;
        this.initialized = false;
        this.speechSessionEventToken = null;
        this.speechToTextPartialResultToken = null;
        this.speechToTextFinalResultToken = null;
        this.speechErrorEventToken = null;
        this.speechQualityEventToken = null;
        this.useDebugDictation = false;
        this.responseVersion = VoiceResponseVersion.version2;
        this.runtimeManager = runtimeManager;
        this.fdSpeechClientObserver = fdSpeechClientObserver;
        this.useFrontdoorWorkflow = useFrontdoorWorkflow;
        this.startRecognitionInSpeechToTextClient =
            startRecognitionInSpeechToTextClient;
        this.enableSetAnnotationsFromALVoiceClient =
            enableSetAnnotationsFromALVoiceClient;
        this.annotationObserver = {
            onAnnotationResult: function (op) {
                _this.onAnnotationResult(op);
            },
        };
        // add various properties based on query-string parameters
        var queryString = new URLSearchParams((_a = window.location.search) === null || _a === void 0 ? void 0 : _a.toLowerCase());
        var dictationProps = (_b = DictationSettingsManager.dictationSettings.properties) !== null && _b !== void 0 ? _b : {};
        this.useDebugDictation = queryString.get("wddictationdebugmode") === "true";
        if (this.useDebugDictation) {
            dictationProps.EnableAudioLogging = "true";
        }
        var wdSpeechDomain = queryString.get("wdspeechdomain");
        if (wdSpeechDomain) {
            dictationProps.wdSpeechDomain = decodeURIComponent(wdSpeechDomain);
        }
        DictationSettingsManager.dictationSettings = {
            properties: dictationProps,
        };
    }
    SpeechToTextClient.logPhraseId_old = function (phraseId, connectionId, sessionId, messageType) {
        if (phraseId) {
            sendTelemetryEvent({
                name: "Office.Voice.Online.PhraseReceived",
                dataFields: [
                    { name: "PhraseID", string: phraseId },
                    { name: "ConnectionID", string: connectionId },
                    { name: "SessionID", string: sessionId },
                    { name: "MessageType", string: messageType },
                ],
            });
        }
    };
    SpeechToTextClient.logPhraseId = function (messageType, sessionTelemetry, annotation) {
        var _a;
        var phraseId = (_a = annotation.properties.JsonResult) === null || _a === void 0 ? void 0 : _a.Id;
        if (!phraseId || !sessionTelemetry || !annotation) {
            return;
        }
        var event = {
            name: "Office.Voice.Online.PhraseReceived",
            dataFields: [
                { name: "PhraseID", string: phraseId },
                { name: "ConnectionID", string: sessionTelemetry.connectionID },
                { name: "SessionID", string: sessionTelemetry.sessionID },
                { name: "MessageType", string: messageType },
                // voiceLatency is latency measured within workflow:voice
                { name: "voiceLatency", int64: annotation.properties.audioLatency },
            ],
        };
        if (annotation.properties.commandLatency) {
            // commandLatency is latency measured within workflow:voice-commanding
            event.dataFields.push({
                name: "commandLatency",
                int64: annotation.properties.commandLatency,
            });
        }
        // when possible, include locally measured latency
        if (sessionTelemetry.audioPerfTracker &&
            annotation.properties.JsonResult &&
            annotation.properties.JsonResult.Offset &&
            annotation.properties.JsonResult.Duration) {
            // measure local latency
            var offset = annotation.properties.JsonResult.Offset / 1e4;
            var duration = annotation.properties.JsonResult.Duration / 1e4;
            var isFinal = messageType === "final";
            var latency = sessionTelemetry.audioPerfTracker.measureLatency(offset, duration, isFinal);
            // add to telemetry
            event.dataFields.push({
                name: "perceivedLatency",
                int64: latency.perceived,
            });
            event.dataFields.push({
                name: "serviceLatency",
                int64: latency.service,
            });
        }
        sendTelemetryEvent(event);
    };
    SpeechToTextClient.prototype.initializeAsync = function (subscribeToResultTypes, sessionTelemetry) {
        return __awaiter(this, void 0, void 0, function () {
            var annotationsActive, error_1, msg, err;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        annotationsActive = this.checkAnnotationActive();
                        ULS.sendTraceTag(0x200c3320 /* tag_6ddm6 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "SpeechToTextClient.initializeAsync - initialized: ".concat(this.initialized, ", annotationsActive: ").concat(annotationsActive));
                        if (!annotationsActive) return [3 /*break*/, 2];
                        // release stale annotations before trying to activate again
                        ULS.sendTraceTag(0x2011d392 /* tag_6e3os */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "Releasing old annotations");
                        return [4 /*yield*/, this.uninitializeAsync()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        if (sessionTelemetry)
                            this.sessionTelemetry = sessionTelemetry;
                        _a.label = 3;
                    case 3:
                        _a.trys.push([3, 5, , 6]);
                        // Activate Annotations
                        return [4 /*yield*/, this.activateAnnotations(subscribeToResultTypes)];
                    case 4:
                        // Activate Annotations
                        _a.sent();
                        // Warm up Augloop
                        this.warmUpAugloop();
                        this.initialized = true;
                        return [3 /*break*/, 6];
                    case 5:
                        error_1 = _a.sent();
                        msg = "SpeechToTextClient.initializeAsync - Activate Annotations - " +
                            error_1.message;
                        err = {
                            errorType: AugLoopUtils.AugloopError.ErrorAugloopActivateAnnotations,
                            errorMsg: msg + " - Error stack: " + error_1.stack,
                        };
                        /**
                         * As long as AL Websocket timeout, AL will point client to a new Websocket. However, AL sub/pub keeps sending.
                         * the client "Timeout waiting for response" error message even client is dictation, which doesn't impact the dictation working.
                         * The issue needs to be fixed from infrastructural side,
                         * AL has an User Story to fix the error: https://office.visualstudio.com/DefaultCollection/GSX/_workitems/edit/4779001
                         */
                        if (msg.includes("Timeout waiting for response")) {
                            this.fdSpeechClientObserver.onWarning(err);
                        }
                        else {
                            this.fdSpeechClientObserver.onError(err);
                        }
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    // Uninitialize speech to text client.
    SpeechToTextClient.prototype.uninitializeAsync = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var annotationsActive, tokenVarNames, tokenValues, err_1, msg;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        annotationsActive = this.checkAnnotationActive();
                        ULS.sendTraceTag(0x200c3321 /* tag_6ddm7 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "SpeechToTextClient.uninitializeAsync - initialized: ".concat(this.initialized, ", annotationsActive: ").concat(annotationsActive));
                        this.initialized = false;
                        tokenVarNames = [
                            "speechSessionEventToken",
                            "speechToTextPartialResultToken",
                            "speechToTextFinalResultToken",
                            "speechQualityEventToken",
                            "speechErrorEventToken",
                        ];
                        tokenValues = tokenVarNames
                            .map(function (tokenVar) { return _this[tokenVar]; })
                            .filter(function (token) { return token !== null; });
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, AugLoopUtils.releaseAnnotations(this.runtimeManager, tokenValues)];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _b.sent();
                        msg = "SpeechToTextClient.uninitializeAsync - Release Annotations - " +
                            err_1.message;
                        (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.onAugloopError(msg);
                        ULS.sendTraceTag(0x223096d6 /* tag_8mj1w */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Error, "SpeechToTextClient.uninitializeAsync - Release Annotations - Error message: " +
                            msg +
                            " - Error stack: " +
                            err_1.stack);
                        return [3 /*break*/, 4];
                    case 4:
                        tokenVarNames.forEach(function (tokenVar) { return (_this[tokenVar] = null); });
                        return [2 /*return*/];
                }
            });
        });
    };
    SpeechToTextClient.prototype.checkAnnotationActive = function () {
        return (this.speechSessionEventToken !== null ||
            this.speechToTextPartialResultToken !== null ||
            this.speechToTextFinalResultToken !== null ||
            this.speechQualityEventToken !== null ||
            this.speechErrorEventToken !== null);
    };
    // Return an annotation observer implemented by us that app will use to forward annotations to us.
    SpeechToTextClient.prototype.getAnnotationObserver = function () {
        return this.annotationObserver;
    };
    SpeechToTextClient.prototype.activateAnnotations = function (subscribeToResultTypes) {
        return __awaiter(this, void 0, void 0, function () {
            var speechErrorEventPromise, speechSessionEventPromise, speechToTextPartialResultPromise, speechToTextFinalResultPromise, speechQualityEventPromise;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        speechErrorEventPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechErrorEvent.getTypeName(), this.annotationObserver, this.enableSetAnnotationsFromALVoiceClient);
                        speechSessionEventPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechSessionEvent.getTypeName(), this.annotationObserver, this.enableSetAnnotationsFromALVoiceClient);
                        speechToTextPartialResultPromise = null;
                        if (subscribeToResultTypes.indexOf(SpeechToTextResultType.PartialResult) >= 0) {
                            speechToTextPartialResultPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechToTextPartialResult.getTypeName(), this.annotationObserver, this.enableSetAnnotationsFromALVoiceClient);
                        }
                        speechToTextFinalResultPromise = null;
                        if (subscribeToResultTypes.indexOf(SpeechToTextResultType.FinalResult) >= 0) {
                            speechToTextFinalResultPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechToTextFinalResult.getTypeName(), this.annotationObserver, this.enableSetAnnotationsFromALVoiceClient);
                        }
                        speechQualityEventPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechQualityEvent.getTypeName(), this.annotationObserver, this.enableSetAnnotationsFromALVoiceClient);
                        // Add audio logging property when using debug dictation
                        if (this.useDebugDictation) {
                            /* eslint-disable-next-line no-console */
                            console.warn("This dictation session will be logging audio");
                        }
                        return [4 /*yield*/, Promise.all([
                                speechSessionEventPromise,
                                speechToTextPartialResultPromise,
                                speechToTextFinalResultPromise,
                                speechQualityEventPromise,
                                speechErrorEventPromise,
                            ])];
                    case 1:
                        // Resolve all AnnotationActivation promise asynchronously
                        _a = _b.sent(), this.speechSessionEventToken = _a[0], this.speechToTextPartialResultToken = _a[1], this.speechToTextFinalResultToken = _a[2], this.speechQualityEventToken = _a[3], this.speechErrorEventToken = _a[4];
                        return [2 /*return*/];
                }
            });
        });
    };
    SpeechToTextClient.prototype.warmUpAugloop = function () {
        /**
         * Warm up Augloop with empty VoiceTile to avoid initializing Frontdoor twice in Augloop.
         * */
        AugLoopUtils.startAudioProcessor(this.runtimeManager, this.useFrontdoorWorkflow, DictationSettingsManager.dictationSettings, this.responseVersion);
    };
    // Handle an annotation result.
    SpeechToTextClient.prototype.onAnnotationResult = function (op) {
        var _this = this;
        var speechErrorEventItems = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechErrorEvent.getTypeName());
        speechErrorEventItems.map(function (item) {
            _this.handleSpeechErrorEvent(item.body);
        });
        var speechSessionEventItems = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechSessionEvent.getTypeName());
        speechSessionEventItems.map(function (item) {
            _this.handleSpeechSessionEvent(item.body);
        });
        var speechToTextPartialResults = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechToTextPartialResult.getTypeName());
        speechToTextPartialResults.map(function (item) {
            _this.handleSpeechToTextPartialResult(item.body);
        });
        var speechToTextFinalResults = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechToTextFinalResult.getTypeName());
        speechToTextFinalResults.map(function (item) {
            var body = item.body;
            var finalText = body.text;
            sendSpeechToTextFinalResultToSpeechClientObserver(_this.fdSpeechClientObserver, body, finalText, _this.sessionTelemetry);
        });
        var speechQualityEventItems = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechQualityEvent.getTypeName());
        speechQualityEventItems.map(function (item) {
            var speechQualityEventResult = (item.body);
            if (speechQualityEventResult.eventInfo &&
                speechQualityEventResult.eventInfo.eventName ===
                    IEventName.QualityStatusUpdate) {
                _this.handleSpeechQualityEvent(speechQualityEventResult.eventInfo);
            }
        });
    };
    SpeechToTextClient.prototype.handleSpeechErrorEvent = function (speechErrorEvent) {
        if (!speechErrorEvent)
            return;
        ULS.sendTraceTag(0x2211e863 /* tag_8e479 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, JSON.stringify(speechErrorEvent));
        // Determine what actions should be done in client side based on error state and error impact
        if (speechErrorEvent.ErrorState === SpeechErrorEventState.FINAL &&
            speechErrorEvent.ErrorImpact === SpeechErrorEventImpact.NO_DICTATION) {
            ULS.sendTraceTag(0x2211e861 /* tag_8e477 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Error, JSON.stringify(speechErrorEvent));
            // Call FrontdoorSpeechClientObserver to show user a friendly UI in WAC
            this.fdSpeechClientObserver.onError(speechErrorEvent.ErrorCode);
        }
        else if (speechErrorEvent.ErrorState === SpeechErrorEventState.INTERMITTENT &&
            speechErrorEvent.ErrorImpact === SpeechErrorEventImpact.NO_DICTATION) {
            ULS.sendTraceTag(0x2211e862 /* tag_8e478 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, JSON.stringify(speechErrorEvent));
            /**
             * TODO: Show tooltips to let users know that we meet a problem.
             * There may be another cases that we would like to show user the tooltips such as NoCommanding impact from LUIS, etc
             **/
        }
        else if (speechErrorEvent.ErrorState === SpeechErrorEventState.FINAL &&
            speechErrorEvent.ErrorImpact === SpeechErrorEventImpact.NO_COMMANDING) {
            ULS.sendTraceTag(0x22061009 /* tag_8b7aj */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, JSON.stringify(speechErrorEvent));
            // Show the tooltips to user that the voice commanding is disabled.
            this.fdSpeechClientObserver.showDictationWarning(speechErrorEvent.ErrorCode, speechErrorEvent.ErrorImpact);
        }
    };
    // Handle a voice session event.
    SpeechToTextClient.prototype.handleSpeechSessionEvent = function (speechSessionEvent) {
        var _a;
        switch (speechSessionEvent.eventId) {
            case SpeechSessionEventIds.SpeechRecognitionStarted:
                this.transcriberId = speechSessionEvent.sessionId;
                if (this.useDebugDictation) {
                    /* eslint-disable-next-line no-console */
                    console.warn("Speech transcriberId: ".concat(this.transcriberId));
                }
                // todo: this is temporary. remove once change passing the observer to VoiceSession are in wac
                if (this.startRecognitionInSpeechToTextClient) {
                    this.fdSpeechClientObserver.onSpeechRecognitionStarted();
                }
                break;
            case SpeechSessionEventIds.SpeechRecognitionStopped:
                // set the endReason as speech recognition was ended by server
                (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.setEndReason("SpeechRecognitionStopped");
                this.fdSpeechClientObserver.onSpeechEnd();
                break;
            case SpeechSessionEventIds.SpeechSessionStarted:
                this.speechSessionId = speechSessionEvent.sessionId;
                if (this.useDebugDictation) {
                    /* eslint-disable-next-line no-console */
                    console.warn("Speech speechSessionId: ".concat(this.speechSessionId));
                }
                if (this.fdSpeechClientObserver.onSpeechSessionStarted &&
                    this.speechSessionId) {
                    var speechSessionStarted = {
                        speechSessionId: this.speechSessionId,
                        transcriptionSignature: "",
                    };
                    this.fdSpeechClientObserver.onSpeechSessionStarted(speechSessionStarted);
                }
                break;
            case SpeechSessionEventIds.SpeechSessionStopped:
                // WAC doesn't have SpeechSessionStopped event but Transcription does.
                if (this.fdSpeechClientObserver.onSpeechSessionStopped) {
                    this.fdSpeechClientObserver.onSpeechSessionStopped();
                }
                break;
            default:
                /* eslint-disable-next-line no-console */
                console.warn("Unexpected speechSessionEventId: ".concat(speechSessionEvent.eventId));
                break;
        }
    };
    // Handle a speech to text partial result.
    SpeechToTextClient.prototype.handleSpeechToTextPartialResult = function (speechToTextPartialResult) {
        var _a, _b, _c, _d, _e, _f;
        if (speechToTextPartialResult.text !== "") {
            var text = new Map([
                [speechToTextPartialResult.language, speechToTextPartialResult.text],
            ]);
            (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.onPartialResultAnnotationResponse(Date.now(), (_b = speechToTextPartialResult.properties.JsonResult) === null || _b === void 0 ? void 0 : _b.Offset, (_c = speechToTextPartialResult.properties.JsonResult) === null || _c === void 0 ? void 0 : _c.Duration);
            var properties = new Map();
            properties.set("JsonResult", JSON.stringify(speechToTextPartialResult.properties.JsonResult));
            this.fdSpeechClientObserver.onFragmentText(text, properties);
            // finally, log the phraseId and latencies
            if (dictationUserPerceivedLatencies()) {
                if (this.sessionTelemetry) {
                    SpeechToTextClient.logPhraseId("partial", this.sessionTelemetry, speechToTextPartialResult);
                }
            }
            else {
                SpeechToTextClient.logPhraseId_old((_d = speechToTextPartialResult.properties.JsonResult) === null || _d === void 0 ? void 0 : _d.Id, (_e = this.sessionTelemetry) === null || _e === void 0 ? void 0 : _e.connectionID, (_f = this.sessionTelemetry) === null || _f === void 0 ? void 0 : _f.sessionID, "partial");
            }
        }
    };
    // Handle a speech quality event.
    SpeechToTextClient.prototype.handleSpeechQualityEvent = function (qualityStatusUpdate) {
        this.fdSpeechClientObserver.onEventUpdate(qualityStatusUpdate);
    };
    return SpeechToTextClient;
}());
export { SpeechToTextClient };
/** We use speechToTextFinalResult to get JSON Properties. But final text can come from
 * speechToTextFinalResult or commandResult.textAnnotation. This method extracts required information
 * And sends to onFinalText observer
 */
export function sendSpeechToTextFinalResultToSpeechClientObserver(fdSpeechClientObserver, speechToTextFinalResult, finalText, sessionTelemetry) {
    var _a, _b, _c;
    var text = new Map([
        [speechToTextFinalResult.language, finalText],
    ]);
    if (sessionTelemetry && finalText !== "") {
        sessionTelemetry.onFinalResultAnnotationResponse(Date.now(), (_a = speechToTextFinalResult.properties.JsonResult) === null || _a === void 0 ? void 0 : _a.Offset, (_b = speechToTextFinalResult.properties.JsonResult) === null || _b === void 0 ? void 0 : _b.Duration);
    }
    var properties = new Map();
    properties.set("JsonResult", JSON.stringify(speechToTextFinalResult.properties.JsonResult));
    fdSpeechClientObserver.onFinalText(text, properties);
    // finally, log the phraseId and latencies
    if (dictationUserPerceivedLatencies()) {
        if (sessionTelemetry && finalText !== "") {
            SpeechToTextClient.logPhraseId("final", sessionTelemetry, speechToTextFinalResult);
        }
    }
    else {
        SpeechToTextClient.logPhraseId_old((_c = speechToTextFinalResult.properties.JsonResult) === null || _c === void 0 ? void 0 : _c.Id, sessionTelemetry === null || sessionTelemetry === void 0 ? void 0 : sessionTelemetry.connectionID, sessionTelemetry === null || sessionTelemetry === void 0 ? void 0 : sessionTelemetry.sessionID, "final");
    }
}
//# sourceMappingURL=SpeechToTextClient.js.map